import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import { useNavigate } from '@tanstack/react-router'
import { Check, ChevronDown, EyeOff, Lightbulb } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { newId } from '@/lib/format'
import { useUpdate } from '@/mock/store'
import type { Task } from '@/mock/types'
import { useToast } from '@/shell/toaster'

export type Prompt = {
  /** Stable id; hiding stores it. */
  code: string
  /** A question in the couple's words: "Kde budou hosté spát?" */
  title: string
  /** Why it matters, from their data where possible: "2 pozvánky chtějí ubytování." */
  hint: string
  /** One small button: add a task, open a setting, open a sheet. */
  action: ReactNode
  /** `in-tasks`: the suggested task is open, so the action becomes a quiet "V úkolech". */
  state?: 'open' | 'in-tasks'
}

/**
 * "Na co nezapomenout": questions worth checking in a section, computed from its
 * data and shown only while they matter. The first few show; the rest wait
 * under "Zobrazit další". Each can be hidden. Renders nothing when empty, and
 * screens leave it out for viewers.
 */
export function PromptList({
  title = 'Na co nezapomenout',
  prompts,
  visible = 3,
  onHide,
}: {
  title?: string
  prompts: Prompt[]
  visible?: number
  onHide: (code: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  if (prompts.length === 0) return null
  const shown = expanded ? prompts : prompts.slice(0, visible)
  const more = prompts.length - shown.length

  return (
    <section aria-label={title} className="rounded-panel border border-border bg-card">
      <div className="flex items-center gap-2.5 px-5 pt-4 pb-1 sm:px-6">
        <span className="grid size-7 place-items-center rounded-full bg-warning text-warning-foreground">
          <Lightbulb aria-hidden="true" className="size-3.5" />
        </span>
        <h2 className="text-base font-semibold">{title}</h2>
        <span className="text-sm text-muted-foreground tabular-nums">{prompts.length}</span>
      </div>
      <ul className="divide-y divide-border px-5 sm:px-6">
        {shown.map((prompt) => (
          <li
            key={prompt.code}
            className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:gap-6"
          >
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-sm font-semibold">{prompt.title}</p>
              <p className="text-sm text-muted-foreground">{prompt.hint}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {prompt.state === 'in-tasks' ? (
                <span className="inline-flex min-h-9 items-center gap-1.5 px-3 text-xs font-semibold text-success-foreground">
                  <Check aria-hidden="true" className="size-3.5" /> V úkolech
                </span>
              ) : (
                prompt.action
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    appearance="ghost"
                    tone="neutral"
                    size="icon"
                    className="size-9"
                    onClick={() => onHide(prompt.code)}
                  >
                    <EyeOff aria-hidden="true" className="size-4" />
                    <span className="sr-only">Skrýt: {prompt.title}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Skrýt</TooltipContent>
              </Tooltip>
            </div>
          </li>
        ))}
      </ul>
      {more > 0 || expanded ? (
        <div className="border-t border-border px-3 py-2 sm:px-4">
          <Button
            appearance="ghost"
            tone="neutral"
            size="sm"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Zobrazit méně' : `Zobrazit další (${more})`}
            <ChevronDown
              aria-hidden="true"
              className={cn('size-4 transition-transform', expanded && 'rotate-180')}
            />
          </Button>
        </div>
      ) : (
        <div className="h-1" />
      )}
    </section>
  )
}

/** Where a task prompt stands: no task yet, its task is open, or done (the prompt goes away). */
export function taskPromptState(tasks: Task[], title: string) {
  const related = tasks.filter((task) => task.title === title)
  if (related.some((task) => task.done)) return 'done' as const
  return related.length ? ('in-tasks' as const) : ('open' as const)
}

/** "Přidat úkol" from a prompt: adds the task on top and offers to open it. */
export function useAddPromptTask() {
  const update = useUpdate()
  const { showToast } = useToast()
  const navigate = useNavigate()
  return (title: string, extra: Pick<Task, 'labels' | 'context'>) => {
    const id = newId('task')
    update((current) => ({
      ...current,
      tasks: [
        {
          id,
          title,
          note: '',
          dueDate: null,
          priority: 'normal',
          assigneeId: null,
          done: false,
          ...extra,
        },
        ...current.tasks,
      ],
    }))
    showToast({
      title: 'Úkol přidán',
      description: title,
      tone: 'success',
      action: {
        label: 'Otevřít úkol',
        altText: 'Otevřít přidaný úkol',
        onClick: () => void navigate({ to: `/tasks/${id}` }),
      },
    })
  }
}
